import { useState, createContext, useEffect, useContext } from "react";

import { fetchInternalApi } from "../utils/apiCalls";
import { useAuth } from "../firebase";

const InternalAdminContext = createContext();
export { InternalAdminContext };

const InternalAdminContextProvider = ({ children }) => {
  const [user, userLoading] = useAuth();

  const [isAdminCheckLoading, setIsAdminCheckLoading] = useState(true);
  const [isUserAclymateAdmin, setIsUserAclymateAdmin] = useState(false);
  const [selectedCompany, setSelectedCompany] = useState({});
  const [selectableCompanies, setSelectableCompanies] = useState([]);

  useEffect(() => {
    const checkIfUserIsAclymateAdmin = async () => {
      if (!user) {
        setIsAdminCheckLoading(false);
        return;
      }

      const { claims } = await user.getIdTokenResult({ forceRefresh: true });
      const { isAclymateAdmin = false } = claims;

      setIsUserAclymateAdmin(isAclymateAdmin);
      setIsAdminCheckLoading(false);
    };

    if (!userLoading) {
      checkIfUserIsAclymateAdmin();
    }
  }, [user, userLoading]);

  useEffect(() => {
    const fetchAndSetSelectableCompanies = async () => {
      const allCompanies = await fetchInternalApi({
        endpoint: "adminFetchAllCompanies",
        user
      });

      // A failed admin-token check comes back as a plain { error } object,
      // not an array. Degrade to an empty list rather than crash; the
      // dropdown this feeds just stays empty, which is recoverable.
      if (!Array.isArray(allCompanies)) {
        return setSelectableCompanies([]);
      }

      const allCompaniesWithNames = allCompanies
        .filter(({ name }) => !!name)
        .map(({ name, id }) => ({
          name: `${name} (${id})`,
          id
        }));

      return setSelectableCompanies(allCompaniesWithNames);
    };

    if (isUserAclymateAdmin) {
      fetchAndSetSelectableCompanies();
    }
  }, [isUserAclymateAdmin, user]);

  return (
    <InternalAdminContext.Provider
      value={{
        user,
        userLoading,
        isAdminCheckLoading,
        isUserAclymateAdmin,
        selectedCompany,
        setSelectedCompany,
        selectableCompanies
      }}
    >
      {children}
    </InternalAdminContext.Provider>
  );
};

export const useInternalAdminContext = () => useContext(InternalAdminContext);
export default InternalAdminContextProvider;
